import { createAccountRequestBody } from '@/helpers/accountRequestHelpers';
import { createUserFullName } from '@/helpers/createUserFullName';
import usePermissions from '@/hooks/usePermissions';
import { useGetOrganizationDetailsQuery } from '@/services/organizations';
import { Box, toast } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import { Locale, ProgramType, UserPermissions } from 'types';

import { useCreateAccountMutation } from '../../services/accounts';
import { isErrorWithData } from '../../services/helpers';
import { useGetCurrentUserQuery } from '../../services/users';
import { useAuth } from '../auth/authProvider';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { AccountForm } from './AccountForm';

export const CreateAccount = () => {
  const { profileAsSalesRep: profileAsSalesRepFeatureEnabled = false, comfortPlanDealerConditions } = useFlags();
  const [createAccount] = useCreateAccountMutation();
  const { claims } = useAuth();
  const { data: user } = useGetCurrentUserQuery();
  const { data: organizationData } = useGetOrganizationDetailsQuery(
    {
      alias: claims?.org,
    },
    { skip: !claims?.org },
  );
  const navigate = useNavigate();
  const userPermissions = usePermissions();
  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const createdAccount = await createAccount(createAccountRequestBody(values)).unwrap();
      toast.success('Account created successfully');
      if (createdAccount?.id) {
        if (comfortPlanDealerConditions && userPermissions.includes(UserPermissions.comfortPlanDealer)) {
          navigate(`/accounts/${createdAccount.id}/application/create`);
        } else {
          navigate(`/accounts/${createdAccount.id}`);
        }
      } else {
        navigate('/accounts');
      }
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(errorMessage);
      } else {
        console.error(e);
        toast.error('Error creating account');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const doePrOnlyOrg =
    organizationData?.programTypes?.length === 1 && organizationData?.programTypes[0] === ProgramType.doePr;
  const defaultInitialLanguage = doePrOnlyOrg ? Locale.es : Locale.en;

  const salesRepInitialValues = {
    salesRepEmail: user?.email,
    salesRepName: createUserFullName(user?.givenName, user?.familyName),
    salesRepPhoneNumber: user?.phoneNumber,
  };

  const initialValues = {
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    lat: '',
    lon: '',
    // applicant info
    primary: {
      firstName: '',
      lastName: '',
      middleName: '',
      phoneNumber: '',
      email: '',
    },
    applicantAddress1: '',
    applicantAddress2: '',
    applicantCity: '',
    applicantState: '',
    applicantZip: '',
    // co-applicant info
    secondary: {
      firstName: '',
      lastName: '',
      middleName: '',
      phoneNumber: '',
      email: '',
      applicantAddress1: '',
      applicantAddress2: '',
      applicantCity: '',
      applicantState: '',
      applicantZip: '',
    },
    // sales rep info
    salesRepName: '',
    salesRepLicenseNumber: '',
    salesRepEmail: '',
    reference: '',
    programReferenceId: '',
    programType: '',
    language: defaultInitialLanguage,
    // hvac property details
    homeSize: '',
    existingSystems: '',
    systemsBeingReplaced: 1,
    // utility info
    lseId: '',
    tariffId: '',
    rate: '',
    ...(profileAsSalesRepFeatureEnabled && user?.userIsSalesRep ? salesRepInitialValues : {}),
  };

  return (
    <>
      <Helmet>
        <title>Create Account</title>
      </Helmet>
      <MainContainer>
        <PageHeader title="Create a New Account" />
        <Box
          padding={{
            base: '0 lg',
            tablet: '0',
          }}
        >
          <AccountForm
            initialValues={initialValues}
            handleSubmit={handleSubmit}
            hasAdditionalApplicantData={false}
            programTypes={organizationData?.programTypes ?? []}
            isAccountCreate={true}
          />
        </Box>
      </MainContainer>
    </>
  );
};
